
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './VendingMachine.css';

// ============================================
// FLASK API
// ============================================

const API_URL = 'vending_products';

// ============================================
// ROSA_RELAY BLUETOOTH CONFIGURATION
// ============================================

const BT_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const BT_CHAR_TX_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

// Connect to ROSA_RELAY
async function connectBluetooth() {
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ name: 'ROSA_RELAY' }],
    optionalServices: [BT_SERVICE_UUID],
  });

  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(BT_SERVICE_UUID);
  const characteristic = await service.getCharacteristic(BT_CHAR_TX_UUID);

  return { device, characteristic };
}

// Send ON1, OFF1, ON2, OFF2 etc.
async function sendBTCommand(characteristic, command) {
  const encoder = new TextEncoder();
  await characteristic.writeValue(encoder.encode(command));
}

// Pocket ID mapping: P01 -> Relay 1, P02 -> Relay 2
const getRelayNumberFromPocket = (pocketId) => {
  const match = String(pocketId || '')
    .trim()
    .toUpperCase()
    .match(/^P0?([1-8])$/);

  return match ? Number(match[1]) : null;
};

// ============================================
// IMAGE URL HELPER
// ============================================

const productImageUrl = (image) => {
  if (!image) return '';

  if (/^https?:\/\//i.test(image)) return image;

  return `${process.env.PUBLIC_URL || ''}${image.startsWith('/') ? '' : '/'}${image}`;
};

// ============================================
// MULTIPLE PRODUCT IMAGES
// ============================================

const getProductImages = (product) => {
  let images = product.images || product.image_urls || [];

  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch {
      images = images.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }

  if (!Array.isArray(images)) images = [];

  const result = images
    .map((item) =>
      typeof item === 'string'
        ? item
        : item?.image_url || item?.url || item?.image
    )
    .filter(Boolean);

  if (product.image && !result.includes(product.image)) {
    result.unshift(product.image);
  }

  return [...new Set(result)];
};

// ============================================
// CURRENCY FORMATTER
// ============================================

const money = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

// ============================================
// PRODUCT IMAGE COMPONENT
// ============================================

function ProductImage({ image, name, className = '' }) {
  const [failed, setFailed] = useState(!image);

  useEffect(() => {
    setFailed(!image);
  }, [image]);

  return (
    <div className={`vm-image-wrap ${className}`}>
      {!failed && image ? (
        <img
          src={productImageUrl(image)}
          alt={name || 'Product'}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="vm-image-placeholder">
          <span>IMAGE UNAVAILABLE</span>
          {name && <small>{name}</small>}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function VendingMachine() {
  // ------------------------------------------
  // PRODUCTS
  // ------------------------------------------

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ------------------------------------------
  // SEARCH AND CATEGORY
  // ------------------------------------------

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Products');

  // ------------------------------------------
  // SELECTED PRODUCT
  // ------------------------------------------

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // ------------------------------------------
  // BASKET
  // Only one selected product at a time
  // ------------------------------------------

  const [cart, setCart] = useState([]);

  // ------------------------------------------
  // CHECKOUT
  // ------------------------------------------

  const [showCheckout, setShowCheckout] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);

  // ------------------------------------------
  // IMAGE PREVIEW
  // ------------------------------------------

  const [previewProduct, setPreviewProduct] = useState(null);
  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);

  // ------------------------------------------
  // BLUETOOTH
  // ------------------------------------------

  const btCharacteristicRef = useRef(null);
  const btDeviceRef = useRef(null);
  const activeRelayRef = useRef(null);

  const [btConnected, setBtConnected] = useState(false);
  const [btConnecting, setBtConnecting] = useState(false);
  const [btDeviceName, setBtDeviceName] = useState('');
  const [relayStatus, setRelayStatus] = useState(
    'Connect to ROSA_RELAY to control the vending machine.'
  );

  // ==========================================
  // FETCH PRODUCTS FROM FLASK
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setLoadError('');

        const response = await fetch(API_URL);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !Array.isArray(data.products)) {
          throw new Error(data.message || 'Invalid API response');
        }

        const formattedProducts = data.products.map((product) => {
          const image = product.image_url || product.image || '';

          const images = getProductImages({
            image,
            images: product.images,
            image_urls: product.image_urls,
          });

          return {
            id: product.product_id,
            name: product.product_name,
            category: product.category || 'Other',
            price: Number(product.price) || 0,
            description: product.description || '',
            pocketId: product.pocket_id || '',
            image: images[0] || image,
            images,
          };
        });

        if (isMounted) {
          setProducts(formattedProducts);
        }
      } catch (error) {
        console.error('Product loading error:', error);

        if (isMounted) {
          setLoadError('Unable to load products. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  // ==========================================
  // BLUETOOTH COMMAND
  // ==========================================

  const sendRelayCommand = async (relayNumber, action) => {
    const characteristic = btCharacteristicRef.current;

    if (!characteristic) {
      setRelayStatus('Please connect to ROSA_RELAY first.');
      return false;
    }

    const command = `${action}${relayNumber}`;

    try {
      await sendBTCommand(characteristic, command);

      console.log('Bluetooth command sent:', command);

      setRelayStatus(
        `Relay ${relayNumber}: ${
          action === 'ON' ? 'Activated' : 'Deactivated'
        }`
      );

      return true;
    } catch (error) {
      console.error('Bluetooth command error:', error);
      setRelayStatus(`Failed to send ${command}: ${error.message}`);
      return false;
    }
  };

  // ==========================================
  // ACTIVATE SELECTED PRODUCT RELAY
  // ==========================================

  const activateRelayForProduct = async (product) => {
    const relayNumber = getRelayNumberFromPocket(product?.pocketId);

    if (!relayNumber) {
      setRelayStatus(
        `No valid relay assigned to Pocket ID: ${
          product?.pocketId || 'Not assigned'
        }`
      );
      return;
    }

    if (!btCharacteristicRef.current) {
      setRelayStatus(
        `Product selected. Connect ROSA_RELAY to activate Pocket ${product.pocketId}.`
      );
      return;
    }

    const previousRelay = activeRelayRef.current;

    // Same relay is already ON.
    if (previousRelay === relayNumber) {
      setRelayStatus(`Pocket ${product.pocketId} is already activated.`);
      return;
    }

    // Switch OFF previous relay.
    if (previousRelay) {
      const offSuccess = await sendRelayCommand(previousRelay, 'OFF');

      if (!offSuccess) return;

      activeRelayRef.current = null;
    }

    // Switch ON newly selected relay.
    const success = await sendRelayCommand(relayNumber, 'ON');

    if (success) {
      activeRelayRef.current = relayNumber;

      setRelayStatus(
        `Pocket ${product.pocketId} activated successfully. Relay ${relayNumber} ON.`
      );
    }
  };

  // ==========================================
  // BLUETOOTH CONNECT
  // ==========================================

  const handleBTConnect = async () => {
    if (!navigator.bluetooth) {
      setRelayStatus(
        'Web Bluetooth is not supported. Please use Chrome or Edge.'
      );
      return;
    }

    try {
      setBtConnecting(true);
      setRelayStatus('Searching for ROSA_RELAY...');

      const { device, characteristic } = await connectBluetooth();

      btCharacteristicRef.current = characteristic;
      btDeviceRef.current = device;

      setBtConnected(true);
      setBtDeviceName(device.name || 'ROSA_RELAY');

      setRelayStatus(`Connected to ${device.name || 'ROSA_RELAY'}.`);

      device.addEventListener('gattserverdisconnected', () => {
        btCharacteristicRef.current = null;
        btDeviceRef.current = null;
        activeRelayRef.current = null;

        setBtConnected(false);
        setBtDeviceName('');
        setRelayStatus('Bluetooth device disconnected.');
      });

      // Activate the currently selected product if one exists.
      if (selectedProduct) {
        await activateRelayForProduct(selectedProduct);
      }
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      setRelayStatus(`Bluetooth connection failed: ${error.message}`);
    } finally {
      setBtConnecting(false);
    }
  };

  // ==========================================
  // BLUETOOTH DISCONNECT
  // ==========================================

  const handleBTDisconnect = async () => {
    const activeRelay = activeRelayRef.current;

    if (activeRelay && btCharacteristicRef.current) {
      await sendRelayCommand(activeRelay, 'OFF');
    }

    const device = btDeviceRef.current;

    if (device?.gatt?.connected) {
      device.gatt.disconnect();
    }

    btCharacteristicRef.current = null;
    btDeviceRef.current = null;
    activeRelayRef.current = null;

    setBtConnected(false);
    setBtDeviceName('');
    setRelayStatus('Bluetooth disconnected.');
  };

  // ==========================================
  // DYNAMIC CATEGORIES
  // ==========================================

  const categories = useMemo(
    () => [
      'All Products',
      ...new Set(
        products.map((product) => product.category).filter(Boolean)
      ),
    ],
    [products]
  );

  // ==========================================
  // FILTER PRODUCTS
  // ==========================================

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();

    return products.filter((product) => {
      const matchesCategory =
        category === 'All Products' || product.category === category;

      const searchableText =
        `${product.name} ${product.category} ${product.description} ${product.pocketId}`.toLowerCase();

      return matchesCategory && searchableText.includes(term);
    });
  }, [products, search, category]);

  // ==========================================
  // SELECT PRODUCT
  // IMPORTANT: REPLACE OLD BASKET ITEM
  // ==========================================

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setSelectedImageIndex(0);

    // Keep ONLY the newly selected product.
    // Previous product is replaced, not appended.
    setCart([
      {
        ...product,
        quantity: 1,
      },
    ]);

    // Activate the relay assigned to this product.
    activateRelayForProduct(product);
  };

  // ==========================================
  // SELECT PRODUCT IMAGE
  // ==========================================

  const productImages = selectedProduct
    ? selectedProduct.images?.length
      ? selectedProduct.images
      : selectedProduct.image
        ? [selectedProduct.image]
        : []
    : [];

  const currentSelectedImage =
    productImages[selectedImageIndex] || selectedProduct?.image || '';

  const changeSelectedImage = (direction) => {
    if (!productImages.length) return;

    setSelectedImageIndex((current) => {
      return (
        (current + direction + productImages.length) %
        productImages.length
      );
    });
  };

  // ==========================================
  // CHANGE QUANTITY
  // ==========================================

  const changeQuantity = (id, delta) => {
    setCart((current) =>
      current
        .map((item) =>
          item.id === id
            ? {
                ...item,
                quantity: Math.max(1, item.quantity + delta),
              }
            : item
        )
    );
  };

  const selectedQuantity = cart[0]?.quantity || 1;

  // ==========================================
  // BASKET TOTALS
  // ==========================================

  const itemCount = cart.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const total = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // ==========================================
  // CHECKOUT
  // ==========================================

  const openCheckout = () => {
    if (!cart.length) return;

    setOrderConfirmed(false);
    setShowCheckout(true);
  };

  const confirmDemoOrder = () => {
    if (!cart.length) return;

    setOrderConfirmed(true);
  };

  // ==========================================
  // FINISH ORDER
  // ==========================================

  const finishOrder = async () => {
    const activeRelay = activeRelayRef.current;

    if (activeRelay && btCharacteristicRef.current) {
      await sendRelayCommand(activeRelay, 'OFF');
    }

    activeRelayRef.current = null;

    setCart([]);
    setSelectedProduct(null);
    setSelectedImageIndex(0);
    setShowCheckout(false);
    setOrderConfirmed(false);
    setPreviewProduct(null);
    setIsPreviewZoomed(false);

    setSearch('');
    setCategory('All Products');

    setRelayStatus('Order finished. Active relay switched OFF.');
  };

  // ==========================================
  // IMAGE PREVIEW
  // ==========================================

  const openBasketPreview = (product) => {
    setPreviewProduct(product);
    setIsPreviewZoomed(false);
  };

  const closePreview = () => {
    setPreviewProduct(null);
    setIsPreviewZoomed(false);
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="vm-app">

      {/* HEADER */}

      <header className="vm-header">
        <a
          className="vm-brand"
          href="/vending-machine"
          aria-label="Vending Machine home"
        >
          <div className="vm-brand-mark">V</div>

          <div>
            <div className="vm-brand-name">Vending Machine</div>
            <div className="vm-brand-subtitle">
              CRAFTED WITH CARE � CHOSEN BY YOU
            </div>
          </div>
        </a>

        <div className="vm-header-note">
          <span className="vm-live-dot" />
          SELF-SERVICE KIOSK
        </div>
      </header>

      {/* BLUETOOTH CONNECTION */}

      <section
        aria-label="Vending machine Bluetooth connection"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '12px 18px',
          background: '#fffaf4',
          borderBottom: '1px solid #eadbca',
        }}
      >
        <span
          style={{
            padding: '7px 14px',
            borderRadius: '20px',
            background: btConnected ? '#e7f5e9' : '#fff0e8',
            color: btConnected ? '#236b36' : '#96502d',
            border: `1px solid ${
              btConnected ? '#9acda5' : '#e6b69e'
            }`,
            fontSize: '13px',
            fontWeight: 700,
          }}
        >
          {btConnected
            ? `Bluetooth: ${btDeviceName} connected`
            : 'Bluetooth: Disconnected'}
        </span>

        {!btConnected ? (
          <button
            type="button"
            onClick={handleBTConnect}
            disabled={btConnecting}
            style={{
              border: 0,
              borderRadius: '20px',
              padding: '9px 18px',
              background: btConnecting ? '#b8a18d' : '#76513d',
              color: '#fff',
              fontWeight: 700,
              cursor: btConnecting ? 'wait' : 'pointer',
            }}
          >
            {btConnecting ? 'Connecting...' : 'Connect ROSA_RELAY'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleBTDisconnect}
            style={{
              border: 0,
              borderRadius: '20px',
              padding: '9px 18px',
              background: '#9a4938',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Disconnect
          </button>
        )}

        <span
          role="status"
          style={{
            flexBasis: '100%',
            textAlign: 'center',
            color: '#76513d',
            fontSize: '13px',
          }}
        >
          {relayStatus}
        </span>
      </section>

      {/* HERO */}

      <section className="vm-hero">
        <div className="vm-hero-copy">
          <span className="vm-eyebrow">
            A LITTLE SOMETHING HANDMADE
          </span>

          <h1>
            Discover something
            <br />
            <em>beautiful.</em>
          </h1>

          <p>
            Explore thoughtfully crafted keepsakes, home decor and gifts
            from local artisans.
          </p>

          <div className="vm-hero-pills">
            <span>Artisan picks</span>
            <span>Made with care</span>
          </div>

          <button
            type="button"
            className="vm-hero-button"
            onClick={() =>
              document.getElementById('vm-collection')?.scrollIntoView({
                behavior: 'smooth',
              })
            }
          >
            Explore collection
          </button>
        </div>

        <div className="vm-hero-art">
          <div className="vm-hero-circle" />

          {products[1] && (
            <div className="vm-hero-product vm-hero-product-one">
              <ProductImage
                image={products[1].image}
                name={products[1].name}
              />
            </div>
          )}

          {products[0] && (
            <div className="vm-hero-product vm-hero-product-two">
              <ProductImage
                image={products[0].image}
                name={products[0].name}
              />
            </div>
          )}

          <span className="vm-hero-star vm-hero-star-one"></span>
          <span className="vm-hero-star vm-hero-star-two"></span>
        </div>
      </section>

      {/* MAIN COLLECTION */}

      <main className="vm-main" id="vm-collection">

        <div className="vm-section-heading">
          <div>
            <span className="vm-eyebrow">THE COLLECTION</span>
            <h2>Choose your product</h2>
            <p>
              Select a product image to view its details.
            </p>
          </div>

          <label className="vm-search">
            <span aria-hidden="true"></span>

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search products..."
              aria-label="Search products"
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </label>
        </div>

        {/* CATEGORY FILTER */}

        <div className="vm-category-row" aria-label="Product categories">
          {categories.map((item) => (
            <button
              type="button"
              key={item}
              className={`vm-category ${
                category === item ? 'active' : ''
              }`}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>

        {/* MAIN TWO-COLUMN LAYOUT */}

        <div className="vm-content-layout">

          {/* LEFT: PRODUCT IMAGE LIST */}

          <section
            className="vm-products-section"
            aria-label="Products"
          >
            <div className="vm-results-line">
              <span>
                {loading
                  ? 'Loading products...'
                  : `${filteredProducts.length} products`}
              </span>

              <span>Tap an image to select</span>
            </div>

            <div className="vm-products-grid">

              {loading && (
                <div className="vm-empty">
                  <h3>Loading products...</h3>
                  <p>Please wait while we load the collection.</p>
                </div>
              )}

              {!loading && loadError && (
                <div className="vm-empty">
                  <h3>Products unavailable</h3>
                  <p>{loadError}</p>

                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                  >
                    Try again
                  </button>
                </div>
              )}

              {!loading &&
                !loadError &&
                filteredProducts.map((product) => (
                  <div className="vm-product-card" key={product.id}>
                    <button
                      type="button"
                      className={`vm-product-image-button ${
                        selectedProduct?.id === product.id
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => selectProduct(product)}
                      aria-label={`Select ${product.name}`}
                      aria-pressed={selectedProduct?.id === product.id}
                    >
                      <ProductImage
                        image={product.image}
                        name={product.name}
                        className="vm-card-image"
                      />
                    </button>

                    <div className="vm-product-info">
                      <span className="vm-product-category">
                        {product.category}
                      </span>

                      <h3>{product.name}</h3>

                      <p>{product.description}</p>

                      <div className="vm-product-bottom">
                        <strong>{money(product.price)}</strong>
                      </div>
                    </div>
                  </div>
                ))}

              {!loading &&
                !loadError &&
                filteredProducts.length === 0 && (
                  <div className="vm-empty">
                    <h3>No products found</h3>
                    <p>Try another product name or category.</p>

                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setCategory('All Products');
                      }}
                    >
                      Show all products
                    </button>
                  </div>
                )}
            </div>
          </section>

          {/* RIGHT: SELECTED PRODUCT AND BASKET */}

          <aside className="vm-selected-panel">

            {!selectedProduct ? (
              <div className="vm-cart-empty">
                <div className="vm-empty-basket">?</div>
                <strong>Select a product</strong>
                <p>
                  Choose an image from the product list to view its
                  details and basket.
                </p>
              </div>
            ) : (
              <div className="vm-selected-content">

                {/* SELECTED PRODUCT HEADER */}

                <div className="vm-selected-header">
                  <button
                    type="button"
                    className="vm-back-button"
                    onClick={() => {
                      setSelectedProduct(null);
                      setSelectedImageIndex(0);
                      setCart([]);
                    }}
                  >
                    Back
                  </button>

                  <span className="vm-detail-category">
                    {selectedProduct.category}
                  </span>
                </div>

                {/* PRODUCT IMAGE AND INFORMATION */}

                <div className="vm-selected-product">

                  <div className="vm-selected-image-area">
                    <div className="vm-selected-main-image">
                      <ProductImage
                        image={currentSelectedImage}
                        name={selectedProduct.name}
                      />
                    </div>
                  </div>

                  <div className="vm-selected-information">
                    <h2>{selectedProduct.name}</h2>

                    <strong className="vm-detail-price">
                      {money(selectedProduct.price)}
                    </strong>

                    <p>{selectedProduct.description}</p>

                    <span className="vm-selected-pocket">
                      Pocket ID: {selectedProduct.pocketId || 'Not assigned'}
                    </span>
                  </div>
                </div>

                {/* IMAGE THUMBNAILS */}

                {productImages.length > 1 && (
                  <div className="vm-detail-thumbnails">

                    <button
                      type="button"
                      className="vm-image-arrow"
                      onClick={() => changeSelectedImage(-1)}
                      aria-label="Previous image"
                    >
                     
                    </button>

                    {productImages.map((image, index) => (
                      <button
                        type="button"
                        key={`${image}-${index}`}
                        className={`vm-detail-thumbnail ${
                          selectedImageIndex === index ? 'active' : ''
                        }`}
                        onClick={() => setSelectedImageIndex(index)}
                        aria-label={`View image ${index + 1}`}
                      >
                        <ProductImage
                          image={image}
                          name={selectedProduct.name}
                        />
                      </button>
                    ))}

                    <button
                      type="button"
                      className="vm-image-arrow"
                      onClick={() => changeSelectedImage(1)}
                      aria-label="Next image"
                    >
                      
                    </button>
                  </div>
                )}

                {/* QUANTITY */}

                                {/* TOTAL */}

                <div className="vm-detail-total">
                  <strong>Total Amount</strong>
                  <strong>{money(total)}</strong>
                </div>

                {/* BASKET */}

                <div className="vm-cart-panel">
                  <div className="vm-cart-title">
                    <div>
                      <span className="vm-eyebrow">YOUR SELECTION</span>
                      <h2>My basket</h2>
                    </div>

                    <span className="vm-cart-count">{itemCount}</span>
                  </div>

                  {cart.length > 0 && (
                    <div className="vm-cart-items">
                      {cart.map((item) => (
                        <div className="vm-cart-item" key={item.id}>
                          <button
                            type="button"
                            className="vm-cart-image-preview"
                            onClick={() => openBasketPreview(item)}
                            aria-label={`Preview ${item.name}`}
                          >
                            <ProductImage
                              image={item.image}
                              name={item.name}
                              className="vm-cart-product-image"
                            />
                          </button>

                          <div className="vm-cart-item-main">
                            <strong>{item.name}</strong>
                            <span>{money(item.price)} each</span>

                                                     </div>

                          <strong className="vm-line-total">
                            {money(item.price * item.quantity)}
                          </strong>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="vm-cart-summary">
                    <div className="vm-summary-line">
                      <span>Items</span>
                      <span>{itemCount}</span>
                    </div>

                    <div className="vm-total-row">
                      <strong>Total Amount</strong>
                      <strong>{money(total)}</strong>
                    </div>

                    <button
                      type="button"
                      className="vm-checkout-button"
                      disabled={!cart.length}
                      onClick={openCheckout}
                    >
                      Continue with UPI
                    </button>

                    <p className="vm-secure-note">
                      Demo payment mode
                    </p>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>

      {/* FOOTER */}

      <footer className="vm-footer">
        <span>VENDING MACHINE</span>
        <span>Celebrating the art of handmade.</span>
        <span>DEMO STOREFRONT</span>
      </footer>

      {/* CHECKOUT MODAL */}

      {showCheckout && (
        <div
          className="vm-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowCheckout(false);
            }
          }}
        >
          <section
            className="vm-checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vm-checkout-title"
          >
            <button
              type="button"
              className="vm-modal-close"
              onClick={() => setShowCheckout(false)}
              aria-label="Close checkout"
            >
              �
            </button>

            {!orderConfirmed ? (
              <>
                <span className="vm-eyebrow">SECURE CHECKOUT</span>

                <h2 id="vm-checkout-title">UPI Payment</h2>

                <p className="vm-modal-intro">
                  Review your selected product and total amount.
                </p>

                <div className="vm-modal-order-items">
                  {cart.map((item) => (
                    <div key={item.id}>
                      <span>
                        {item.name} � {item.quantity}
                      </span>

                      <strong>
                        {money(item.price * item.quantity)}
                      </strong>
                    </div>
                  ))}
                </div>

                <div className="vm-modal-total">
                  <span>Total Amount</span>
                  <strong>{money(total)}</strong>
                </div>

                <p className="vm-demo-warning">
                  Demo only. No real payment is initiated or collected.
                </p>

                <button
                  type="button"
                  className="vm-checkout-button vm-pay-button"
                  onClick={confirmDemoOrder}
                >
                  Confirm Demo Order
                </button>
              </>
            ) : (
              <div className="vm-demo-success" role="status">
                <div className="vm-success-icon">?</div>

                <span className="vm-eyebrow">ORDER CONFIRMED</span>

                <h2 id="vm-checkout-title">Thank you!</h2>

                <p>
                  Your demo order for <strong>{money(total)}</strong> has
                  been prepared. No payment was collected.
                </p>

                <button
                  type="button"
                  className="vm-checkout-button"
                  onClick={finishOrder}
                >
                  Finish Order
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {/* BASKET IMAGE PREVIEW */}

      {previewProduct && (
        <div
          className="vm-preview-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closePreview();
            }
          }}
        >
          <div className="vm-preview-modal">
            <button
              type="button"
              className="vm-preview-close"
              onClick={closePreview}
              aria-label="Close image preview"
            >
              x
            </button>

            <div className="vm-preview-heading">
              <strong>{previewProduct.name}</strong>
              <span>{money(previewProduct.price)}</span>
            </div>

            <div className="vm-preview-image-container">
              <img
                src={productImageUrl(previewProduct.image)}
                alt={previewProduct.name}
                className={isPreviewZoomed ? 'zoomed' : ''}
                onClick={() =>
                  setIsPreviewZoomed((current) => !current)
                }
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </div>

            <p className="vm-preview-hint">
              Click the image to zoom in or out
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
